import { useMemo, useState } from 'react';
import type { CoverageLine } from '../api/use-set-advance-coverage';

// One selectable BL in the coverage picker. `appliedFloor` is what the BL has
// already consumed of the advance: a BL with a non-zero floor can neither be
// deselected nor have its share dropped below it (the consuming invoice would
// be left uncovered).
export interface CoverageBl {
  readonly blOrderId: string;
  readonly name: string | null;
  readonly quantityMt: string | null;
  readonly appliedFloor: number;
}

export interface CoverageRowState {
  readonly covered: boolean;
  readonly amount: string;
}

type CoverageMap = Record<string, CoverageRowState>;

// Dot decimal, comma/space as thousands grouping — stripped before parsing so
// a comma is never read as a decimal point (keeps input and display aligned).
function centsOf(amount: string): number {
  const n = Number.parseFloat(amount.replace(/[\s,]/g, ''));
  return Number.isFinite(n) ? Math.round(n * 100) : Number.NaN;
}

function buildInitial(
  rows: readonly CoverageBl[],
  prefill: ReadonlyMap<string, string>,
): CoverageMap {
  const map: CoverageMap = {};
  for (const row of rows) {
    const seeded = prefill.get(row.blOrderId);
    map[row.blOrderId] = {
      covered: seeded !== undefined || row.appliedFloor > 0,
      amount: seeded ?? '',
    };
  }
  return map;
}

// How the shares must relate to the advance total: `exact` is the classic
// sum-to-total invariant; `atMost` (the allocation editor's create path) lets
// part of the pool stay unallocated while still blocking over-allocation.
// Keep in lockstep with the backend advance-coverage-guards.ts.
export type CoverageSumMode = 'exact' | 'atMost';

interface DerivedCoverage {
  readonly totalCents: number;
  readonly allocatedCents: number;
  readonly sumsToTotal: boolean;
  readonly underAllocated: boolean;
  readonly overAllocated: boolean;
  readonly belowFloor: boolean;
  readonly isValid: boolean;
  readonly coverage: CoverageLine[];
}

export interface CoverageState extends DerivedCoverage {
  readonly map: CoverageMap;
  setCovered(blOrderId: string, covered: boolean): void;
  setAmount(blOrderId: string, amount: string): void;
}

// The advance total can carry sub-cent precision (derived from percent ×
// volume), so cent-precision shares often can't tie it exactly. Accept a sum
// within min(1% of the total, 1000 currency units) of it. Keep in lockstep
// with the backend (advance-coverage.ts coverageMatchTolerance).
function toleranceCents(total: number): number {
  return Math.round(Math.min(Math.abs(total) * 0.01, 1000) * 100);
}

function deriveCoverage(
  map: CoverageMap,
  total: number,
  floors: ReadonlyMap<string, number>,
  sumMode: CoverageSumMode,
): DerivedCoverage {
  const totalCents = Math.round(total * 100);
  let allocatedCents = 0;
  let belowFloor = false;
  const coverage: CoverageLine[] = [];
  for (const [blOrderId, row] of Object.entries(map)) {
    if (!row.covered) continue;
    const cents = centsOf(row.amount);
    if (Number.isNaN(cents)) {
      belowFloor = true;
      continue;
    }
    allocatedCents += cents;
    if (cents < Math.round((floors.get(blOrderId) ?? 0) * 100)) belowFloor = true;
    coverage.push({ bl_order_id: blOrderId, amount: cents / 100 });
  }
  const tolerance = toleranceCents(total);
  const sumsToTotal = Math.abs(allocatedCents - totalCents) <= tolerance;
  const overAllocated = allocatedCents - totalCents > tolerance;
  const sumOk = sumMode === 'atMost' ? !overAllocated : sumsToTotal;
  return {
    totalCents,
    allocatedCents,
    sumsToTotal,
    underAllocated: totalCents - allocatedCents > tolerance,
    overAllocated,
    belowFloor,
    isValid: sumOk && !belowFloor && coverage.length > 0,
    coverage,
  };
}

// Drives the coverage picker: per-BL covered flag + money share, the running
// allocated total, and whether the set is savable (sums to the advance total
// and no covered BL sits below its consumed floor). The single-BL case is
// covered by construction — the caller seeds its whole share and marks it
// read-only.
export function useCoverageState(
  rows: readonly CoverageBl[],
  total: number,
  prefill: ReadonlyMap<string, string>,
  sumMode: CoverageSumMode = 'exact',
): CoverageState {
  const [map, setMap] = useState<CoverageMap>(() => buildInitial(rows, prefill));

  const floors = useMemo(() => {
    const out = new Map<string, number>();
    for (const row of rows) out.set(row.blOrderId, row.appliedFloor);
    return out;
  }, [rows]);

  const derived = useMemo(
    () => deriveCoverage(map, total, floors, sumMode),
    [map, total, floors, sumMode],
  );

  return {
    map,
    ...derived,
    setCovered: (blOrderId, covered) => {
      setMap((prev) => ({
        ...prev,
        [blOrderId]: { amount: prev[blOrderId]?.amount ?? '', covered },
      }));
    },
    setAmount: (blOrderId, amount) => {
      setMap((prev) => ({
        ...prev,
        [blOrderId]: { covered: prev[blOrderId]?.covered ?? false, amount },
      }));
    },
  };
}
